import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, PiggyBank, TrendingUp, Calendar } from "lucide-react";
import Modal from "../components/Modal";
import ProgressBar from "../components/ProgressBar";
import SummaryCard from "../components/SummaryCard";
import { getSavings, createSavingsGoal, updateSavingsGoal, contributeToGoal, deleteSavingsGoal } from "../utils/api";
import { fmt } from "../utils/calculations";
import { differenceInDays, parseISO, format } from "date-fns";

const EMPTY_GOAL = { name: "", targetAmount: "", currentAmount: "", targetDate: "" };
const EMPTY_CONTRIB = { amount: "", note: "" };

export default function SavingsGoals() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goalModal, setGoalModal] = useState(false);
  const [contribModal, setContribModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_GOAL);
  const [contrib, setContrib] = useState(EMPTY_CONTRIB);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedGoal, setExpandedGoal] = useState(null);

  async function load() {
    setLoading(true);
    try { setGoals(await getSavings()); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_GOAL);
    setError("");
    setGoalModal(true);
  }

  function openEdit(g) {
    setEditing(g);
    setForm({
      name: g.name || "",
      targetAmount: g.targetAmount?.toString() || "",
      currentAmount: g.currentAmount?.toString() || "",
      targetDate: g.targetDate || "",
    });
    setError("");
    setGoalModal(true);
  }

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSaveGoal(e) {
    e.preventDefault();
    setError("");
    if (!form.name || !form.targetAmount) {
      setError("Name and target amount are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateSavingsGoal(editing.id, form);
        setGoals((prev) => prev.map((g) => (g.id === editing.id ? { ...g, ...updated } : g)));
      } else {
        const created = await createSavingsGoal(form);
        setGoals((prev) => [created, ...prev]);
      }
      setGoalModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleContribute(e) {
    e.preventDefault();
    setError("");
    if (!contrib.amount || parseFloat(contrib.amount) <= 0) {
      setError("Enter a positive contribution amount.");
      return;
    }
    setSaving(true);
    try {
      await contributeToGoal(contribModal.id, contrib);
      const updated = goals.map((g) =>
        g.id === contribModal.id
          ? {
              ...g,
              currentAmount: (g.currentAmount || 0) + parseFloat(contrib.amount),
              contributions: [
                ...(g.contributions || []),
                { amount: parseFloat(contrib.amount), note: contrib.note, date: new Date().toISOString() },
              ],
            }
          : g
      );
      setGoals(updated);
      setContribModal(null);
      setContrib(EMPTY_CONTRIB);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteSavingsGoal(id);
      setGoals((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteConfirm(null);
    }
  }

  function daysUntil(dateStr) {
    if (!dateStr) return null;
    try {
      return differenceInDays(parseISO(dateStr), new Date());
    } catch { return null; }
  }

  const totalSaved = goals.reduce((s, g) => s + (g.currentAmount || 0), 0);
  const totalTarget = goals.reduce((s, g) => s + (g.targetAmount || 0), 0);
  const completedGoals = goals.filter((g) => (g.currentAmount || 0) >= g.targetAmount).length;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Savings Goals</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Track progress toward each financial goal</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <Plus className="w-4 h-4" /> Add Goal
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <SummaryCard title="Total Saved" value={fmt(totalSaved)} subtitle="across all goals" icon={PiggyBank} color="green" />
        <SummaryCard title="Total Target" value={fmt(totalTarget)} subtitle="combined goals" icon={TrendingUp} color="blue" />
        <SummaryCard title="Goals Completed" value={`${completedGoals} / ${goals.length}`} subtitle="fully funded" icon={PiggyBank} color="purple" />
      </div>

      {/* Goals grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : goals.length === 0 ? (
        <div className="card text-center py-16">
          <PiggyBank className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No savings goals yet</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Create your first goal to start tracking progress.</p>
          <button onClick={openAdd} className="btn-primary inline-flex">
            <Plus className="w-4 h-4" /> Add Goal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {goals.map((g) => {
            const pct = g.targetAmount > 0 ? Math.min(100, ((g.currentAmount || 0) / g.targetAmount) * 100) : 0;
            const days = daysUntil(g.targetDate);
            const isComplete = (g.currentAmount || 0) >= g.targetAmount;
            const isExpanded = expandedGoal === g.id;

            return (
              <div key={g.id} className={`card flex flex-col gap-4 ${isComplete ? "border-secondary-200 dark:border-secondary-800/50 bg-secondary-50 dark:bg-secondary-900/20" : ""}`}>
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{g.name}</h3>
                    {g.targetDate && (
                      <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>
                          {format(parseISO(g.targetDate), "MMM d, yyyy")}
                          {days !== null && (
                            <span className={`ml-1 ${days < 30 ? "text-red-500" : days < 90 ? "text-yellow-600 dark:text-yellow-400" : "text-gray-400 dark:text-gray-500"}`}>
                              ({days > 0 ? `${days}d left` : days === 0 ? "today!" : "overdue"})
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(g)} className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteConfirm(g.id)} className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Progress */}
                <div>
                  <ProgressBar
                    value={g.currentAmount || 0}
                    max={g.targetAmount || 1}
                    color={isComplete ? "green" : "blue"}
                    size="md"
                  />
                </div>

                {/* Amounts */}
                <div className="flex justify-between items-center text-sm">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Saved</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{fmt(g.currentAmount || 0)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {fmt(Math.max(0, (g.targetAmount || 0) - (g.currentAmount || 0)))}
                    </p>
                  </div>
                </div>

                {isComplete && (
                  <div className="flex items-center gap-2 p-2 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg text-sm text-secondary-700 dark:text-secondary-400 font-medium">
                    <PiggyBank className="w-4 h-4" />
                    Goal reached!
                  </div>
                )}

                {/* Actions */}
                {!isComplete && (
                  <button
                    onClick={() => { setContribModal(g); setContrib(EMPTY_CONTRIB); setError(""); }}
                    className="btn-secondary w-full justify-center text-secondary-700 dark:text-secondary-400 border-secondary-200 dark:border-secondary-800/50 hover:bg-secondary-50 dark:hover:bg-secondary-900/20"
                  >
                    <TrendingUp className="w-4 h-4" /> Log Contribution
                  </button>
                )}

                {/* Contribution history toggle */}
                {(g.contributions || []).length > 0 && (
                  <div>
                    <button
                      onClick={() => setExpandedGoal(isExpanded ? null : g.id)}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {isExpanded ? "Hide" : "Show"} history ({g.contributions.length})
                    </button>
                    {isExpanded && (
                      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {[...g.contributions].reverse().map((c, i) => (
                          <div key={i} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400 py-1 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <span>{format(new Date(c.date), "MMM d")} {c.note && <span className="text-gray-400 dark:text-gray-500">— {c.note}</span>}</span>
                            <span className="font-medium text-secondary-600 dark:text-secondary-400">+{fmt(c.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Goal Modal */}
      <Modal open={goalModal} onClose={() => setGoalModal(false)} title={editing ? "Edit Goal" : "Add Savings Goal"}>
        <form onSubmit={handleSaveGoal} className="space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}
          <div>
            <label className="label">Goal name *</label>
            <input className="input" name="name" placeholder="e.g. Emergency Fund" value={form.name} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Target amount *</label>
              <input className="input" type="number" name="targetAmount" min="0" step="0.01" placeholder="10000"
                value={form.targetAmount} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Amount already saved</label>
              <input className="input" type="number" name="currentAmount" min="0" step="0.01" placeholder="0.00"
                value={form.currentAmount} onChange={handleChange} />
            </div>
          </div>
          <div>
            <label className="label">Target date</label>
            <input className="input" type="date" name="targetDate" value={form.targetDate} onChange={handleChange} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setGoalModal(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Goal"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Contribution Modal */}
      <Modal open={!!contribModal} onClose={() => setContribModal(null)} title={`Add Contribution — ${contribModal?.name}`} size="sm">
        <form onSubmit={handleContribute} className="space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}
          <div>
            <label className="label">Contribution amount *</label>
            <input className="input" type="number" min="0.01" step="0.01" placeholder="0.00"
              value={contrib.amount} onChange={(e) => setContrib((c) => ({ ...c, amount: e.target.value }))} />
          </div>
          <div>
            <label className="label">Note (optional)</label>
            <input className="input" placeholder="e.g. Monthly transfer" value={contrib.note}
              onChange={(e) => setContrib((c) => ({ ...c, note: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setContribModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Saving…" : "Log Contribution"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Goal" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">Are you sure you want to delete this savings goal?</p>
        <div className="flex gap-3">
          <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger flex-1 justify-center">Delete</button>
        </div>
      </Modal>
    </div>
  );
}
